/**
 * Geometry-free vision config schema primitives.
 *
 * These zod schemas are shared between routine-runner's vision layer
 * (`LocateTypes`, `VisionInterface`) and the skill-config layer in `@sb/ai`.
 * They intentionally live in their own leaf module that imports ONLY `zod`.
 *
 * `LocateTypes` and `VisionInterface` both pull in `@sb/geometry` (for
 * `CartesianPose`), which transitively requires `three` at module-load time.
 * Lightweight backend services such as `updater-bot` import `@sb/ai` but do
 * not bundle `three`, so any runtime edge from `@sb/ai` into those modules
 * crashes with `Cannot find module 'three'`. Keeping these primitives here
 * lets `@sb/ai` consume them without dragging in geometry/three.
 *
 * `LocateTypes` and `VisionInterface` re-export these names, so existing
 * importers are unaffected.
 */
import * as zod from 'zod';

/**
 * How a 2D detection becomes a 6D pose — the single discriminator behind every
 * Locate projection, named for what each method measures rather than for how it
 * is implemented. Maps 1:1 onto vision-bot's `ProjectionMethod` oneof, so there
 * is no translation layer between what an operator picked and what goes on the
 * wire.
 *
 *   ManualPlane    position and orientation from the taught plane.
 *   DetectedPlane  position and orientation from a surface fitted to the depth
 *                  within each match's support.
 *   DetectedDepth  position from the depth at the top of each match's support,
 *                  orientation from the world axes — depth measured, surface
 *                  not. This is the operator's "Detect surface tilt" switch,
 *                  turned off.
 *   Detection3D    position and orientation from the 3D pose estimator, which
 *                  has its own RPC (`Run3DPoseEstimation`) rather than an arm
 *                  of the projection oneof — its request shares no fields with
 *                  the other three.
 */
export const ProjectionMethodEnum = zod.enum([
  'ManualPlane',
  'DetectedPlane',
  'DetectedDepth',
  'Detection3D',
]);

export type ProjectionMethod = zod.infer<typeof ProjectionMethodEnum>;

/**
 * Whether a projection method measures the scene from depth rather than
 * projecting onto taught geometry. `ManualPlane` is the only one that does not,
 * and it is also the default an unset method falls back to.
 *
 * Callers that also need depth for reasons of their own — the `Detection3D`
 * detection *method*, or a preview that already holds a plane — OR their own
 * condition onto this one.
 */
export function projectionMethodNeedsDepth(
  method: ProjectionMethod | undefined,
): boolean {
  return (
    (method ?? ProjectionMethodEnum.enum.ManualPlane) !==
    ProjectionMethodEnum.enum.ManualPlane
  );
}

export const ResultOrdering = zod.enum([
  // Scan order (corner-based diagonal sweep)
  'top-left-to-bottom-right',
  'top-right-to-bottom-left',
  'bottom-left-to-top-right',
  'bottom-right-to-top-left',
  // By property (always descending)
  'confidence-desc',
  'area-desc',
  // By world height, highest first ("Top down"). Unlike the orderings above it
  // reads each match's projected pose, not its 2D detection, so it can only be
  // applied AFTER projection (see `applyPostProjectionOrdering` in
  // `resultOrdering.ts`) and is a no-op on `ManualPlane`, where every match
  // lands at the same taught-plane height.
  'world-z-desc',
]);

export type ResultOrdering = zod.infer<typeof ResultOrdering>;

// DinoFast detection method primitives
export const DinoFastPrecision = zod.enum(['fast', 'standard', 'fine']);

export type DinoFastPrecision = zod.infer<typeof DinoFastPrecision>;

/**
 * Precision a *new* ClickFind config should start at for a given runner.
 *
 * The tiers are patch-grid resolutions (32/48/96 patches square, i.e.
 * 512/768/1536 px into a 16px-patch DINOv3), and their cost is quadratic in
 * that number. An empty `gpuTaskRunnerId` means the encoder runs on the
 * control box's CPU, where 'standard' is the highest tier that keeps a locate
 * interactive; a GPU Task Runner has the headroom for 'fine'.
 *
 * Creation-time only — an operator's explicit choice is never overwritten.
 */
export function defaultDinoFastPrecision(
  gpuTaskRunnerId: string,
): DinoFastPrecision {
  return gpuTaskRunnerId ? 'fine' : 'standard';
}

/**
 * The `precision` patch a runner switch should apply, if any.
 *
 * Follows the new runner's default tier while the operator is still sitting on
 * the old runner's default; an explicit choice is theirs to keep, so this
 * returns an empty patch and leaves `precision` untouched. Spread into the
 * config update: `...carryOverDinoFastPrecision(p, oldId, newId)`.
 *
 * Shared by every teach surface that offers a runner picker, so the tier
 * boundary stays defined in one place.
 */
export function carryOverDinoFastPrecision(
  currentPrecision: DinoFastPrecision,
  oldGpuTaskRunnerId: string,
  newGpuTaskRunnerId: string,
): { precision?: DinoFastPrecision } {
  if (currentPrecision !== defaultDinoFastPrecision(oldGpuTaskRunnerId)) {
    return {};
  }

  return { precision: defaultDinoFastPrecision(newGpuTaskRunnerId) };
}

export const DinoFastReferenceEmbedding = zod.object({
  values: zod.array(zod.number()),
  /**
   * Id of the ClickFind training take this reference was taught on, when it came
   * from one. Lets deleting a take take its teaching with it, instead of leaving
   * embeddings behind that nothing in the gallery accounts for.
   *
   * Optional and never sent to vision-bot: references taught before takes were
   * recorded (or by a save whose frame could not be captured) simply carry no
   * owner, and the proto mappers copy `values` alone.
   */
  takeId: zod.string().optional(),
});

export type DinoFastReferenceEmbedding = zod.infer<
  typeof DinoFastReferenceEmbedding
>;

export const RegionOfInterest = zod.object({
  top: zod.number(),
  bottom: zod.number(),
  left: zod.number(),
  right: zod.number(),
});

export type RegionOfInterest = zod.infer<typeof RegionOfInterest>;

export const DEFAULT_REGION_OF_INTEREST: RegionOfInterest = {
  top: 0,
  right: 0,
  bottom: 0,
  left: 0,
};

// Shared 3D-pose-estimation config. Consumed by both routine-runner's
// `Detection3DParams` (which adds 2D-detection fields) and the skill-config
// layer's `ProjectionConfig.detection3d` in `@sb/ai`. Geometry-free (zod only).
// primitiveDimX/Y/Z and fpfhVoxelSize are in METERS. cadModelUnits is separate:
// it records the unit system of an uploaded CAD mesh file, not the units of
// fields here.
export const Detection3DPoseEstimationConfig = zod.object({
  poseMethod: zod
    .enum(['OBB_DEPTH', 'OBB_ICP', 'FPFH_ICP'])
    .default('OBB_DEPTH'),
  cadSource: zod.enum(['NONE', 'PRIMITIVE', 'CAD_FILE']).default('NONE'),
  primitiveShapeType: zod.enum(['BOX', 'CYLINDER']).default('BOX'),
  // Primitive dimensions are in METERS and feed the vision-bot pose RPC; a
  // zero/negative/Infinite dimension is a degenerate primitive, so bound them
  // (a bare `z.number()` would accept all three).
  primitiveDimX: zod.number().positive().finite().default(0.1),
  primitiveDimY: zod.number().positive().finite().default(0.1),
  primitiveDimZ: zod.number().positive().finite().default(0.1),
  cadModelStorageID: zod.string().optional(),
  cadModelFileName: zod.string().optional(),
  cadModelFormat: zod.enum(['STL', 'OBJ', 'PLY']).optional(),
  cadModelUnits: zod
    .enum(['METERS', 'MILLIMETERS', 'CENTIMETERS', 'INCHES'])
    .default('MILLIMETERS'),
  confidenceThreshold: zod.number().min(0).max(1).default(0.5),
  maxDetections: zod.number().int().positive().default(10),
  depthOutlierPercentile: zod.number().min(0).max(100).default(2),
  // Minimum ICP / RANSAC inlier-ratio (0–1) required to publish a 3D pose.
  // Detections below this threshold are dropped (not just hidden), so
  // downstream motion and UI overlays never see a low-quality fit. 0
  // disables filtering entirely. The default 0.5 drops clearly-wrong poses
  // (~8 mm+ alignment error) across OBB_ICP and FPFH_ICP; for strict
  // FPFH-with-multi-start use cases, 0.7–0.8 is typical. OBB_DEPTH does
  // not report a fit quality, so the filter is a no-op for that method.
  minInlierRatio: zod.number().min(0).max(1).default(0.5),
  // FPFH_ICP only: number of RANSAC+ICP attempts per crop. The estimator
  // keeps the one with highest ICP fitness (bad-basin rate ≈ p_bad^N).
  // Higher values trade latency for reliability. Ignored by other methods.
  fpfhRansacRestarts: zod.number().int().positive().default(4),
  // FPFH_ICP only: voxel size (meters) for scene+ref downsampling and
  // FPFH feature support. Small parts (<5 cm) prefer 0.001–0.002; larger
  // parts (>20 cm) prefer 0.005–0.01. Ignored by other methods.
  fpfhVoxelSize: zod.number().positive().finite().default(0.003),
});

export type Detection3DPoseEstimationConfig = zod.infer<
  typeof Detection3DPoseEstimationConfig
>;

// What a custom checkpoint runs as. vision-bot reads which from the file.
export const CustomCheckpointModelType = zod.enum([
  'YoloV11',
  'YoloV8-OBB',
  'RF-DETR-Nano',
  'RF-DETR-Small',
  'RF-DETR-Medium',
  'RF-DETR-Large',
  'RT-DETR',
]);
